import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { customAlphabet } from "nanoid";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Generate 4-character reservation code (only letters and numbers, no symbols)
const nanoid = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789', 4);
export function generateReservationCode(): string {
  return nanoid();
}

// Format date to YYYY-MM-DD in local timezone
// This ensures dates match the business's local time, not UTC
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Format time to HH:mm
export function formatTime(date: Date): string {
  return date.toTimeString().slice(0, 5);
}

// Parse date and time to timestamp
export function parseDateTime(date: string, time: string): number {
  return new Date(`${date}T${time}`).getTime();
}

// Format currency (Turkish Lira)
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
  }).format(amount);
}

/**
 * Parse Turkish currency string to number
 * Handles formats like:
 * - "15.000" → 15000 (thousand separator is .)
 * - "15.000,50" → 15000.50 (decimal separator is ,)
 * - "1.234.567" → 1234567 (multiple thousand separators)
 * - "9.876.543,21" → 9876543.21 (multiple thousand seps + decimal)
 * - "12,75" → 12.75
 * - "1234" → 1234
 * - "₺15.000,50" → 15000.50
 * @param value - Currency string in Turkish format
 * @returns Parsed number or NaN if invalid
 */
export function parseTurkishCurrency(value: string): number {
  if (!value || typeof value !== 'string') return NaN;
  
  // Remove currency symbol, spaces, and trim
  let cleaned = value.replace(/[₺\s]/g, '').trim();
  
  if (!cleaned) return NaN;
  
  // Check if it has both . and , (e.g., "15.000,50" or "1.234.567,89")
  const hasDot = cleaned.includes('.');
  const hasComma = cleaned.includes(',');
  
  if (hasDot && hasComma) {
    // Turkish format: dots are thousand separators, comma is decimal
    // Remove all dots, replace comma with dot
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasComma && !hasDot) {
    // Only comma: could be "12,75" (decimal) 
    // Check if comma position suggests decimal (less than 3 digits after comma)
    const parts = cleaned.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      // It's a decimal separator
      cleaned = cleaned.replace(',', '.');
    } else {
      // Could be thousand separator without decimals (unlikely in Turkish but handle it)
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasDot && !hasComma) {
    // Only dots: could be thousand separators like "15.000" or "1.234.567"
    // Or could be decimal like "15.50"
    const parts = cleaned.split('.');
    
    // Check if all parts after the first have exactly 3 digits (thousand separator pattern)
    // e.g., "1.234.567" → ["1", "234", "567"] → all middle/end parts are 3 digits
    const isThousandPattern = parts.length >= 2 && 
      parts.slice(1).every(part => part.length === 3);
    
    if (isThousandPattern) {
      // All dots are thousand separators: remove them all
      cleaned = cleaned.replace(/\./g, '');
    }
    // Otherwise treat as decimal (already correct format for single dot like "15.50")
  }
  
  const result = parseFloat(cleaned);
  return isNaN(result) ? NaN : result;
}

/**
 * Format number as Turkish currency input (for controlled inputs)
 * @param value - Number to format
 * @returns Formatted string without currency symbol
 */
export function formatCurrencyInput(value: number): string {
  if (isNaN(value)) return '';
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

// Format timestamp to date and time
export function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleString("tr-TR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Format timestamp to relative time
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  
  if (diff < minute) return "Az önce";
  if (diff < hour) return `${Math.floor(diff / minute)} dakika önce`;
  if (diff < day) return `${Math.floor(diff / hour)} saat önce`;
  if (diff < week) return `${Math.floor(diff / day)} gün önce`;
  
  return new Date(timestamp).toLocaleDateString("tr-TR");
}

// Calculate estimated completion time
export function calculateEstimatedCompletion(
  startTime: number,
  durationMinutes: number
): number {
  return startTime + durationMinutes * 60 * 1000;
}

// Check if time slot is available
export function isSlotAvailable(
  date: string,
  time: string,
  existingAppointments: Array<{ scheduledDate: string; scheduledTime: string; durationMinutes: number }>,
  maxConcurrent: number = 3,
  durationMinutes: number = 60
): boolean {
  const newStart = parseDateTime(date, time);
  const newEnd = newStart + durationMinutes * 60 * 1000;
  
  let concurrent = 0;
  
  for (const apt of existingAppointments) {
    const aptStart = parseDateTime(apt.scheduledDate, apt.scheduledTime);
    const aptEnd = aptStart + apt.durationMinutes * 60 * 1000;
    
    // Check if new slot overlaps with existing appointment
    // Overlap occurs when: newEnd > aptStart AND newStart < aptEnd
    if (newEnd > aptStart && newStart < aptEnd) {
      concurrent++;
    }
  }
  
  return concurrent < maxConcurrent;
}

// Get available time slots for a day
export function getAvailableSlots(
  date: string,
  durationMinutes: number,
  existingAppointments: Array<{ scheduledDate: string; scheduledTime: string; durationMinutes: number }>,
  openTime: string = "09:00",
  closeTime: string = "19:00",
  maxConcurrent: number = 3
): string[] {
  const slots: string[] = [];
  
  const [openHour, openMin] = openTime.split(":").map(Number);
  const [closeHour, closeMin] = closeTime.split(":").map(Number);
  
  const closeTimeInMinutes = closeHour * 60 + closeMin;
  
  let currentHour = openHour;
  let currentMin = openMin;
  
  while (currentHour < closeHour || (currentHour === closeHour && currentMin < closeMin)) {
    const timeStr = `${String(currentHour).padStart(2, "0")}:${String(currentMin).padStart(2, "0")}`;
    
    // Calculate end time for this slot
    const currentTimeInMinutes = currentHour * 60 + currentMin;
    const endTimeInMinutes = currentTimeInMinutes + durationMinutes;
    
    // Only include slot if it fits within business hours
    if (endTimeInMinutes <= closeTimeInMinutes && 
        isSlotAvailable(date, timeStr, existingAppointments, maxConcurrent, durationMinutes)) {
      slots.push(timeStr);
    }
    
    // Increment by 30 minutes
    currentMin += 30;
    if (currentMin >= 60) {
      currentMin = 0;
      currentHour++;
    }
  }
  
  return slots;
}

// Get status label in Turkish
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: "Bekliyor",
    confirmed: "Onaylandı",
    queued: "Sıraya Alındı",
    in_progress: "Başladı",
    completed: "Tamamlandı",
    cancelled: "İptal Edildi",
  };
  
  return labels[status] || status;
}

// Get vehicle type label in Turkish
export function getVehicleTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    sedan: "Binek Araç",
    suv: "SUV",
    large: "Büyük Araç",
  };
  
  return labels[type] || type;
}

// Get expense category label in Turkish
export function getExpenseCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    materials: "Malzeme",
    labor: "İşçilik",
    rent: "Kira",
    other: "Diğer",
  };
  
  return labels[category] || category;
}

// Generate time slots for display
export function generateTimeSlots(
  startHour: number = 9,
  endHour: number = 19,
  intervalMinutes: number = 30
): string[] {
  const slots: string[] = [];
  
  for (let hour = startHour; hour < endHour; hour++) {
    for (let min = 0; min < 60; min += intervalMinutes) {
      slots.push(`${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}`);
    }
  }
  
  return slots;
}

// Check if date is today
export function isToday(dateStr: string): boolean {
  return dateStr === formatDate(new Date());
}

// Check if date is in the past
export function isPast(dateStr: string): boolean {
  return new Date(dateStr) < new Date(formatDate(new Date()));
}

// Get next available date (tomorrow)
export function getNextAvailableDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return formatDate(tomorrow);
}

// Validate plate number (basic Turkish plate format)
export function isValidPlate(plate: string): boolean {
  // Basic validation: 2 digits, 1-3 letters, 2-4 digits
  const plateRegex = /^\d{2}\s?[A-Z]{1,3}\s?\d{2,4}$/i;
  return plateRegex.test(plate.replace(/\s/g, ""));
}

// Format plate number
export function formatPlate(plate: string): string {
  return plate.toUpperCase().replace(/\s/g, "");
}

/**
 * Normalize Turkish phone number to standard format (05xxxxxxxxx)
 * Accepts formats like:
 * - 05334580556
 * - 5334580556
 * - +905334580556
 * - 905334580556
 * - 00905334580556
 * - 0533 458 05 56
 * - 533 458 05 56
 * - +90 533 458 05 56
 * @param phone - Phone number in any format
 * @returns Normalized phone number (05xxxxxxxxx) or null if invalid
 */
export function normalizePhoneNumber(phone: string): string | null {
  if (!phone) return null;
  
  // Remove all non-digit characters (spaces, dashes, parentheses, plus signs)
  let digits = phone.replace(/\D/g, "");
  
  // Remove country code prefixes
  // Handle: 00905XXXXXXXXX (14 digits)
  if (digits.startsWith("0090") && digits.length === 14) {
    digits = digits.substring(4); // Remove '0090', leaving 05XXXXXXXXX
  }
  // Handle: 905XXXXXXXXX (12 digits)
  else if (digits.startsWith("90") && digits.length === 12) {
    digits = digits.substring(2); // Remove '90', leaving 05XXXXXXXXX
  }
  
  // Add leading 0 if missing (10-digit numbers starting with 5)
  if (digits.length === 10 && digits.startsWith("5")) {
    digits = "0" + digits; // Convert 5XXXXXXXXX to 05XXXXXXXXX
  }
  
  // Validate: should be 11 digits starting with 05
  if (digits.length === 11 && digits.startsWith("05")) {
    return digits;
  }
  
  return null;
}

/**
 * Validate Turkish phone number
 * @param phone - Phone number to validate
 * @returns true if valid Turkish mobile number
 */
export function isValidTurkishPhone(phone: string): boolean {
  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return false;
  
  // Accept all Turkish mobile numbers starting with 05
  // More lenient - just check format (11 digits starting with 05)
  return normalized.length === 11 && normalized.startsWith("05");
}

/**
 * Format phone number for display (0533 458 05 56)
 * @param phone - Phone number to format
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return phone;
  
  // Format as: 0533 458 05 56
  return `${normalized.substring(0, 4)} ${normalized.substring(4, 7)} ${normalized.substring(7, 9)} ${normalized.substring(9, 11)}`;
}

/**
 * SORUN #6 FIX: Normalize Turkish license plate to standard format
 * Removes spaces and converts to uppercase for storage
 * @param plate - License plate in any format (e.g., "34 abc 123", "34ABC123")
 * @returns Normalized plate (e.g., "34ABC123") or original if invalid
 */
export function normalizeLicensePlate(plate: string): string {
  if (!plate) return "";
  
  // Remove all spaces and convert to uppercase
  return plate.trim().toUpperCase().replace(/\s+/g, "");
}

/**
 * SORUN #6 FIX: Format license plate for display (34 ABC 123)
 * @param plate - Normalized plate (e.g., "34ABC123")
 * @returns Formatted plate (e.g., "34 ABC 123") for better readability
 */
export function formatLicensePlate(plate: string): string {
  if (!plate) return "";
  
  // Normalize first
  const normalized = normalizeLicensePlate(plate);
  
  // Turkish plate format: XX ABC YYY (2 digits, 1-3 letters, 1-4 digits)
  const match = normalized.match(/^(\d{2})([A-Z]{1,3})(\d{1,4})$/);
  
  if (match) {
    return `${match[1]} ${match[2]} ${match[3]}`;
  }
  
  // Return as-is if format doesn't match
  return normalized;
}

/**
 * Compress image file using Canvas API (browser-native, no external libs)
 * Reduces file size and dimensions for faster upload and storage
 * @param file - Original image file
 * @param maxWidth - Maximum width (default: 1920px)
 * @param maxHeight - Maximum height (default: 1080px)
 * @param quality - JPEG quality 0-1 (default: 0.8)
 * @returns Compressed image file
 */
export async function compressImage(
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1080,
  quality: number = 0.8
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }
      
      let width = img.width;
      let height = img.height;
      
      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            URL.revokeObjectURL(img.src);
            resolve(compressedFile);
          } else {
            reject(new Error('Image compression failed'));
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Image load failed'));
    };
  });
}
